"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { AtSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Alert from "@/components/ui/alert";

type State = {
  error?: string | null;
  success?: boolean;
};

export function ForgotPasswordForm() {
  const [state, setState] = useState<State>({ error: null, success: false });
  const [isPending, setIsPending] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);

  useEffect(() => {
    if (state?.success) {
      setButtonDisabled(true);
      const timer = setTimeout(() => {
        setButtonDisabled(false);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [state]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isPending || buttonDisabled) return;

    setIsPending(true);
    setState({ error: null, success: false });

    const formData = new FormData(event.currentTarget);
    const email = formData.get("email") as string;

    try {
      const response = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 429) {
          setState({
            error: "Haz intentado esto demasiadas veces. Por favor, inténtalo de nuevo más tarde.",
          });
        } else {
          setState({ error: data.error || "Ha ocurrido un error inesperado." });
        }
      } else {
        setState({ success: true });
      }
    } catch (error) {
      setState({ error: "No se pudo conectar con el servidor. Inténtalo de nuevo más tarde." });
    } finally {
      setIsPending(false);
    }
  };

  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
      <Input
        id="email"
        name="email"
        type="email"
        autoComplete="email"
        placeholder="Correo electrónico"
        autoFocus
        required
        icon={<AtSign />}
      />

      {state?.error && <Alert type="error">{state.error}</Alert>}
      {state?.success && (
        <Alert type="success">
          Hemos enviado un enlace a tu correo electrónico para restablecer tu contraseña
        </Alert>
      )}

      <div className="flex justify-center mt-6">
        <Button type="submit" loading={isPending} disabled={isPending || buttonDisabled}>
          Enviar enlace
        </Button>
      </div>

      <div className="text-center mt-8">
        <Link href="/login" className="font-medium text-link hover:text-accent/80">
          Volver al inicio de sesión
        </Link>
      </div>
    </form>
  );
}
